// ==========================================
// LOGIN BORAFRETE — FLUXO LENDÁRIO
// ✅ Apenas "Entrar com Google" + Login existente
// ✅ Remove botão "Cadastrar-se" (evita erro sessão expirada)
// ✅ Google cuida de tudo: cadastro + login + qualquer email
// ✅ Fluxo: Google → /auth/callback → /selecionar-tipo → Cadastro → Painel
// ==========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { 
  Eye, EyeOff, Mail, Lock, Truck, 
  ArrowRight, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const ModalEsqueciSenha = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Digite seu email'); return; }
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/redefinir-senha`
      });
      if (error) throw error;
      setEnviado(true);
      toast.success('Email de recuperação enviado!');
    } catch (err) {
      toast.error('Erro ao enviar email: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">Recuperar Senha</h3>
          <p className="text-gray-500 text-sm mt-1">Digite seu email para redefinir a senha</p>
        </div>

        {enviado ? (
          <div className="text-center">
            <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-green-700 font-medium">Email enviado!</p>
            <button onClick={onClose} className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Fechar</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} 
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="seu@email.com" required />
              </div>
            </div>
            <button type="submit" disabled={loading} 
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUser } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEsqueciSenha, setShowEsqueciSenha] = useState(false);
  const [imgErro, setImgErro] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const mensagemCadastro = location.state?.mensagem;

  useEffect(() => {
    setIsVisible(true);
    if (mensagemCadastro) {
      toast.success(mensagemCadastro, { duration: 5000 });
    }
  }, [mensagemCadastro]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('Erro login Google:', err);
      toast.error('Erro ao conectar com Google: ' + err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (authError) {
        setError(authError.message || 'Email ou senha incorretos');
        toast.error(authError.message || 'Email ou senha incorretos');
        setLoading(false);
        return;
      }

      if (!authData.user) {
        setError('Erro ao autenticar. Tente novamente.');
        toast.error('Erro ao autenticar. Tente novamente.');
        setLoading(false);
        return;
      }

      const { data: perfil, error: perfilError } = await supabase
        .from('profiles')
        .select('tipo, role, nome, cadastro_aprovado')
        .eq('id', authData.user.id)
        .single();

      if (perfilError) {
        console.warn('⚠️ Erro ao buscar perfil:', perfilError);
      }

      await refreshUser();
      toast.success(`Bem-vindo! ${perfil?.nome || authData.user.email}`);

      const tipo = perfil?.tipo;
      const role = perfil?.role;

      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (tipo === 'empresa' || tipo === 'transportadora') {
        navigate('/perfil');
      } else if (tipo === 'motorista') {
        navigate('/painel');
      } else {
        navigate('/selecionar-tipo');
      }

    } catch (err) {
      console.error('❌ Erro no login:', err);
      setError('Erro ao fazer login. Tente novamente.');
      toast.error('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ==========================================
          LADO ESQUERDO — IMAGEM MOTORISTA LIMPA
          ========================================== */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/boracomagente.jpg), linear-gradient(to bottom right, #1e3a5f, #0f172a)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/30" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-start p-10 h-full w-full">
          <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
            <img 
              src="/logoborinho45.png" 
              alt="BoraFrete" 
              className="h-28 w-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)] hover:drop-shadow-[0_4px_30px_rgba(249,115,22,0.4)] transition-all duration-500"
              onError={() => setImgErro(true)}
            />
            {imgErro && (
              <div className="flex items-center gap-3">
                <Truck className="w-10 h-10 text-orange-400" />
                <span className="text-4xl font-bold text-white">
                  Bora<span className="text-orange-400">Frete</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==========================================
          LADO DIREITO — LOGIN LENDÁRIO
          ========================================== */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-gray-50">
        <div className="w-full max-w-md">
          
          {/* Logo mobile */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logoborinho45.png" 
              alt="BoraFrete" 
              className="h-20 mx-auto mb-2 drop-shadow-lg"
              onError={(e) => { 
                e.target.style.display = 'none'; 
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center gap-2">
              <Truck className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">BoraFrete</span>
            </div>
            <p className="text-gray-500 mt-1">Acesse sua conta</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Olá!</h2>
              <p className="text-gray-500 mt-1">Faça login para continuar</p>
            </div>

            {/* ==========================================
                ✅ ENTRAR COM GOOGLE — ÚNICA OPÇÃO DE CADASTRO
                Aceita Gmail, Hotmail, Outlook, Yahoo...
                ========================================== */}
            <button onClick={handleGoogleLogin} disabled={loading}
              className="w-full py-3 px-4 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-all flex items-center justify-center gap-3 mb-6">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.85 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.67 2.84c.86-2.6 3.3-4.53 6.15-4.53z"/>
              </svg>
              Entrar com Google
            </button>

            {/* ✅ Texto explicativo */}
            <p className="text-xs text-gray-400 text-center mb-4">
              Use sua conta Google (Gmail, Hotmail, Outlook, Yahoo...)
            </p>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">ou use sua conta</span></div>
            </div>

            {/* FORMULÁRIO — SÓ PRA QUEM JÁ TEM CONTA */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="seu@email.com" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Digite sua senha" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={() => setShowEsqueciSenha(true)} 
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium">Esqueci minha senha</button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Entrando...</> : <>Entrar <ArrowRight className="w-5 h-5" /></>}
              </button>
            </form>

            {/* ✅ REMOVIDO: Botão "Cadastrar-se" que dava erro */}
            {/* Agora só entra pelo Google ou já tem conta */}

            <Link to="/" className="mt-6 text-center text-sm text-gray-400 hover:text-gray-600 block">
              ← Voltar para home
            </Link>
          </div>
        </div>
      </div>

      <ModalEsqueciSenha isOpen={showEsqueciSenha} onClose={() => setShowEsqueciSenha(false)} />
    </div>
  );
}